import { z } from 'zod';

/**
 * Zod schema for validating required environment variables
 */
const envSchema = z.object({
  VITE_ENVIRONMENT: z.enum(['simulated', 'production']).default('production'),

  // Microsoft Azure App Registration Configuration
  VITE_AUTH_AUTHORITY: z.url('VITE_AUTH_AUTHORITY must be a valid URL'),
  VITE_AUTH_CLIENT_ID: z.string().min(1, 'VITE_AUTH_CLIENT_ID is required'),

  // Application Insights Configuration (optional)
  VITE_APPINSIGHTS_CONNECTION_STRING: z.string().optional(),
});

/**
 * Validates and parses environment variables
 * Throws an error with detailed information if validation fails
 */
function validateEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const errors = z.treeifyError(result.error);
    console.error('❌ Invalid environment variables:', errors);

    // Create a user-friendly error message
    const errorMessages = Object.entries(errors)
      .filter(([key]) => key !== '_errors')
      .map(([key, value]) => {
        const messages = (value as { _errors: string[] })._errors;
        return `  - ${key}: ${messages.join(', ')}`;
      })
      .join('\n');

    throw new Error(
      `Environment validation failed:\n${errorMessages}\n\nPlease check your .env file and ensure all required variables are set.`
    );
  }

  return result.data;
}

/**
 * Validated and typed environment variables
 * Import this instead of using import.meta.env directly
 */
export const env = validateEnv();

/**
 * Type for environment variables (useful for TypeScript consumers)
 */
export type Env = z.infer<typeof envSchema>;
